/**
 * ============================================================================
 * useRevenueData - REVENUE DATA FETCHING HOOK
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. THIS IS THE SINGLE SOURCE OF TRUTH FOR REVENUE DATA FETCHING
 *    - ALL revenue data MUST come through this hook
 *    - NEVER use vendorApi.getEarningsSummary() directly in components
 *    - If you need revenue data, import and use THIS hook
 *
 * 2. TANSTACK QUERY HANDLES ALL CACHING AUTOMATICALLY
 *    - NEVER add localStorage caching
 *    - NEVER add manual cache keys or timestamps
 *    - NEVER add setInterval polling (use refetchInterval)
 *    - TanStack Query is smarter than you think
 *
 * 3. QUERY KEY STRUCTURE IS CRITICAL
 *    - ['earnings', venueId] is the base key
 *    - Adding filters to key triggers new fetch: ['earnings', venueId, dateFrom, dateTo]
 *    - Changing key invalidates old cache and fetches fresh data
 *    - NEVER change the key structure without understanding implications
 *
 * 4. REFETCH BEHAVIOR
 *    - staleTime: 30 seconds = Data is considered fresh for 30 sec
 *    - refetchInterval: 30 seconds = Auto-refetch every 30 sec in background
 *    - refetchOnWindowFocus: true = Refetch when user returns to tab
 *    - This gives real-time feel for payout updates
 *
 * 5. THIS HOOK WORKS FOR BOTH VENDOR AND SUPER ADMIN
 *    - Vendor: Pass single venueId (number)
 *    - Super Admin: Create separate hook useAllVenuesRevenue() later
 *    - Don't try to make this hook handle both cases
 *
 * 6. ERROR HANDLING
 *    - Errors are returned in 'error' property
 *    - Component handles UI for errors
 *    - Hook only fetches data, doesn't show UI
 *
 * ============================================================================
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type { EarningsApiResponse } from '../types/revenue';

// Debug logging for revenue data
const DEBUG_REVENUE = false;
const logRevenue = (action: string, details?: any) => {
  if (!DEBUG_REVENUE) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(
    `%c[${timestamp}] 💰 REVENUE ${action}`,
    action === 'FETCH' ? 'color: #ef4444; font-weight: bold' :
    action === 'CACHE_HIT' ? 'color: #22c55e; font-weight: bold' :
    'color: #3b82f6',
    details || ''
  );
};

interface UseRevenueDataOptions {
  dateFrom?: string;
  dateTo?: string;
  source?: 'all' | 'spotts' | 'manual';
  // Future: Add sport filter if backend supports it
}

/**
 * Fetches revenue/earnings data for a venue
 *
 * @param venueId - The venue ID to fetch data for
 * @param options - Optional filters (date range, etc.)
 * @returns TanStack Query result with earnings data
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { data, isLoading, error } = useRevenueData(venue?.id);
 *
 * // With date filters (triggers new API call)
 * const { data, isLoading, error } = useRevenueData(venue?.id, {
 *   dateFrom: '2025-01-01',
 *   dateTo: '2025-12-31'
 * });
 *
 * // In component
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * return <RevenueStats data={data} />;
 * ```
 */
export function useRevenueData(
  venueId: number | undefined,
  options: UseRevenueDataOptions = {}
) {
  const { dateFrom, dateTo, source = 'all' } = options;
  const queryClient = useQueryClient();

  // Build the query key
  const queryKey = ['earnings', venueId, dateFrom, dateTo, source];

  // Log cache state when component mounts
  const existingData = queryClient.getQueryData(queryKey);
  const queryState = queryClient.getQueryState(queryKey);

  logRevenue('MOUNT', {
    queryKey,
    hasCachedData: !!existingData,
    cacheState: queryState?.status,
    isStale: queryState?.isInvalidated,
    dataUpdatedAt: queryState?.dataUpdatedAt ? new Date(queryState.dataUpdatedAt).toISOString() : 'never',
    note: 'Compare this key to prefetch key - they must match exactly!'
  });

  return useQuery<EarningsApiResponse, Error>({
    // Query key includes venueId and filters
    // When filters change, TanStack Query automatically fetches new data
    queryKey,

    // Query function - the actual API call
    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      const startTime = performance.now();
      logRevenue('FETCH', { queryKey, reason: 'No cache or cache is stale' });

      // TODO: Backend doesn't support date filters yet
      // When implemented, pass dateFrom/dateTo to API
      const data = await vendorApi.getEarningsSummary(venueId, source);

      const duration = (performance.now() - startTime).toFixed(1);
      logRevenue('FETCHED', { queryKey, duration: `${duration}ms` });

      return data;
    },

    // Only run query if venueId exists
    enabled: !!venueId,

    // Data is considered fresh for 10 seconds (production: need fresh data)
    staleTime: 10 * 1000, // 10 seconds

    // Keep data in cache for 5 minutes even when not mounted
    gcTime: 5 * 60 * 1000, // 5 minutes

    // Don't auto-refetch on interval - too aggressive
    // User can manually refresh if needed
    refetchInterval: false,

    // Refetch when user switches back to browser tab (ensures fresh data)
    refetchOnWindowFocus: true,

    // Refetch on mount to ensure data is current (new bookings appear immediately)
    refetchOnMount: true,

    // Keep previous data while fetching new data
    // Prevents UI flicker when filters change
    placeholderData: (previousData) => previousData,

    // Retry failed requests 1 time before showing error
    retry: 1,

    // Wait 1 second before retry
    retryDelay: 1000,
  });
}

/**
 * Type guard to check if earnings data is loaded
 */
export function hasEarningsData(
  data: EarningsApiResponse | undefined
): data is EarningsApiResponse {
  return !!data && typeof data.total_revenue === 'number';
}

/**
 * Helper to extract summary data from earnings response
 */
export function getRevenueSummary(data: EarningsApiResponse | undefined) {
  if (!hasEarningsData(data)) return null;

  return {
    total_revenue: data.total_revenue,
    vendor_earnings: data.vendor_earnings,
    platform_commission: data.platform_commission,
    vendor_share_percentage: data.vendor_share_percentage,
    platform_share_percentage: data.platform_share_percentage,
    outstanding_debt: data.outstanding_debt,
    net_earnings: data.net_earnings,
    total_refunds: data.total_refunds,
    refund_count: data.refund_count,
  };
}
